/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */

/**
 * Usage:
 *    <div>
 *      <Scrollable>
 *        <JSXContent />
 *      </Scrollable>
 *    </div>
 *
 * IMPORTANT: In order for this to function, you MUST have the parent component
 *            have "display: flex" and "flex-grow: 1".
 */

import React, { useRef } from 'react';
import { css, StyleSheet } from 'aphrodite';
import { mixins } from '@blend/consumer-components';
import { debounce } from '@utils/lodash';

const styles = StyleSheet.create({
  scrollableWrapper: {
    position: 'relative',
    flexGrow: 1,
    flexShrink: 1,
  },
  scrollableContent: {
    ...mixins.fillContainer(),
  },
});

export namespace Scrollable {
  export interface Props {
    onScroll?: (newYPos: number) => void;
  }
}

export const DEBOUNCE_WAIT_TIME_MS = 16;

export const Scrollable: React.FC<Scrollable.Props> = ({ children, onScroll = () => {} }) => {
  const ref = useRef<HTMLDivElement>(null);

  const onScrollableScroll = debounce(() => {
    onScroll(ref?.current?.scrollTop || 0);
  }, DEBOUNCE_WAIT_TIME_MS);

  return (
    <div className={css(styles.scrollableWrapper)}>
      <div
        ref={ref}
        data-it="scrollableContent"
        className={css(styles.scrollableContent)}
        onScroll={onScrollableScroll}
      >
        {children}
      </div>
    </div>
  );
};
